import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Ban, CalendarClock, ChevronDown, ChevronRight, Calculator, Paperclip, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import { useDialogs } from '../../components/Dialogs';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';
import { ExitBadge } from './exitUi';
import { dateWithYear, longDate, viewUrl } from './docUtils';

// HR → Joiners & leavers → Exits. A resignation reaches HR by email: HR records
// it here with the date of the email and the email itself (a screenshot or a
// PDF) as evidence. The same form records any other exit HR decides on.

const FILE_TYPES = 'application/pdf,image/png,image/jpeg,image/webp';
const MAX_BYTES = 10 * 1024 * 1024;
const me = () => JSON.parse(localStorage.getItem('user') || '{}');

const RecordForm = ({ users, kinds, kindLabels, today, onDone, onCancel, say }) => {
    const [f, setF] = useState({ userId: '', kind: 'RESIGNATION', resignedOn: today, lastWorkingDay: '', reason: '', shortfallRecovery: '' });
    const [file, setFile] = useState(null);
    const [plan, setPlan] = useState(null);
    const [planError, setPlanError] = useState(null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const viewer = me();
    const { confirm, dialogs } = useDialogs();
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));
    const resignation = f.kind === 'RESIGNATION';
    // What the preview was worked out for. Anything changed since means the preview is out of date.
    const planKey = [f.userId, f.kind, f.resignedOn || today, f.lastWorkingDay, f.shortfallRecovery].join('|');

    useEffect(() => {
        if (!f.userId) return undefined;
        let live = true;
        const params = { userId: f.userId, kind: f.kind, resignedOn: f.resignedOn || today };
        if (f.lastWorkingDay) params.lastWorkingDay = f.lastWorkingDay;
        if (f.shortfallRecovery) params.shortfallRecovery = f.shortfallRecovery;
        api.get('/lifecycle/exit/plan', { params })
            .then(r => { if (live) { setPlan({ ...r.data, key: planKey }); setPlanError(null); } })
            .catch(e => { if (live) { setPlan(null); setPlanError(errorText(e, 'Could not work out the notice')); } });
        return () => { live = false; };
    }, [planKey, f.userId, f.kind, f.resignedOn, f.lastWorkingDay, f.shortfallRecovery, today]);

    // Only a preview for exactly what is on the form counts.
    const current = f.userId && plan && plan.key === planKey ? plan : null;
    const p = current?.plan;
    // A resignation recorded late can have a standard last day that has already passed: HR must say which day.
    const needsDay = !!p && !f.lastWorkingDay && p.lastWorkingDay < today;

    const pick = (e) => {
        const chosen = e.target.files[0] || null;
        if (chosen && chosen.size > MAX_BYTES) { say(false, 'The file must be under 10 MB'); e.target.value = ''; setFile(null); return; }
        setFile(chosen);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!p) return;
        const name = users.find(u => u.id === f.userId)?.name;
        const day = f.lastWorkingDay || p.lastWorkingDay;
        if (!await confirm({ title: `Record ${kindLabels[f.kind].toLowerCase()} for ${name}?`, message: `${longDate(day)} is the last working day. It takes effect straight away: they go on notice and their exit checklist starts.`, confirmText: 'Record' })) return;
        setBusy(true);
        try {
            const data = new FormData();
            data.append('userId', f.userId);
            data.append('kind', f.kind);
            data.append('reason', f.reason);
            data.append('lastWorkingDay', day);
            if (resignation) data.append('resignedOn', f.resignedOn || today);
            if (resignation && f.shortfallRecovery) data.append('shortfallRecovery', f.shortfallRecovery);
            if (file) data.append('file', file);
            const r = await api.post('/lifecycle/exit/initiate', data, { headers: { 'Content-Type': 'multipart/form-data' } });
            say(true, `Recorded the exit for ${name}.${r.data.exitCaseId ? '' : ' No exit checklist matched, so start one with "Start a checklist" at the top of this page.'}`);
            onDone();
        } catch (err) {
            say(false, errorText(err, 'Could not record the exit'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 12 }} aria-label="Record an exit">
            {dialogs}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Employee</span>
                    <select aria-label="Employee leaving" required style={field} value={f.userId} onChange={e => setF(prev => ({ ...prev, userId: e.target.value, shortfallRecovery: '' }))}>
                        <option value="">Choose…</option>
                        {users.filter(u => u.isActive !== false && (u.status || 'ACTIVE') === 'ACTIVE').map(u => {
                            const own = u.id === viewer.id;
                            const adminOnly = u.role === 'ADMIN' && viewer.role !== 'ADMIN';
                            return <option key={u.id} value={u.id} disabled={own || adminOnly}>{u.name}{own ? ' (you: someone else records your exit)' : adminOnly ? ' (Admin: only an Admin can record this)' : ''}</option>;
                        })}
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Kind</span>
                    <select aria-label="Kind of exit" style={field} value={f.kind} onChange={e => set('kind', e.target.value)}>
                        {kinds.map(k => <option key={k} value={k}>{kindLabels[k]}</option>)}
                    </select>
                </label>
                {resignation && (
                    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Date of their email</span>
                        <input type="date" aria-label="Date of the resignation email" required max={today} style={field} value={f.resignedOn} onChange={e => set('resignedOn', e.target.value)} />
                    </label>
                )}
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Last working day {p && !needsDay && !f.lastWorkingDay ? `(standard: ${dateWithYear(p.lastWorkingDay)})` : ''}</span>
                    <input type="date" aria-label="Last working day" min={today} required={needsDay} style={field} value={f.lastWorkingDay} onChange={e => set('lastWorkingDay', e.target.value)} />
                </label>
            </div>

            {planError && <p role="alert" style={{ color: '#ef4444', margin: 0 }}>{planError}</p>}
            {needsDay && <p role="alert" style={{ color: '#d97706', margin: 0 }}>The standard last day ({dateWithYear(p.lastWorkingDay)}) has already passed. Enter the last working day that was agreed.</p>}
            {p && !needsDay && (
                <div style={{ padding: 12, border: rowBorder, borderRadius: 8, color: 'var(--text-main)', display: 'grid', gap: 6 }} data-testid="notice-plan">
                    <div>Notice under your rules: <strong>{p.noticeDays} days</strong>{p.onProbation ? ' (shorter: probation)' : ''}{resignation ? `, so the standard last day is ${longDate(p.noticeEnd)}` : ''}.</div>
                    {resignation && p.shortfallDays > 0 && (
                        <>
                            <div style={{ color: '#d97706' }}>{f.lastWorkingDay ? longDate(p.lastWorkingDay) : 'This date'} is {p.shortfallDays} day{p.shortfallDays === 1 ? '' : 's'} before the notice ends.</div>
                            <label style={{ display: 'grid', gap: 4, maxWidth: 420 }}><span style={muted}>The shortfall in the final settlement</span>
                                <select aria-label="Notice shortfall" style={field} value={f.shortfallRecovery || p.recovery} onChange={e => set('shortfallRecovery', e.target.value)}>
                                    <option value="RECOVER">Recover {p.shortfallDays} days' pay</option>
                                    <option value="WAIVED">Waive it</option>
                                </select>
                            </label>
                            {!current.shortfallRecoveryOn && <div style={muted}>Recovery is switched off in your lifecycle rules, so nothing is deducted either way.</div>}
                        </>
                    )}
                    {resignation && p.extraDays > 0 && <div style={muted}>{p.extraDays} days beyond the notice period.</div>}
                </div>
            )}

            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>{resignation ? 'Reason given (from their email)' : 'Reason (kept on the record)'}</span>
                <textarea aria-label="Reason for the exit" required maxLength={2000} style={{ ...field, minHeight: 70 }} value={f.reason} onChange={e => set('reason', e.target.value)} />
            </label>
            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>{resignation ? 'Their email: a screenshot or PDF (optional, kept for HR only)' : 'A supporting file (optional, kept for HR only)'}</span>
                <input ref={fileRef} type="file" aria-label="Email screenshot or PDF" accept={FILE_TYPES} onChange={pick} />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={primary} disabled={busy || !p || needsDay}><LogOut size={14} /> {busy ? 'Recording…' : 'Record exit'}</button>
                <button type="button" style={ghost} onClick={onCancel}>Cancel</button>
            </div>
        </form>
    );
};

const SETTLEMENT = { DRAFT: 'Settlement: draft', PENDING_APPROVAL: 'Settlement: waiting for approval', APPROVED: 'Settlement: approved', PAID: 'Settlement: settled', CLOSED: 'Settlement: closed', CANCELLED: 'Settlement: cancelled' };

const ExitRow = ({ e, onChanged, say }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const attach = useRef(null);
    const viewer = me();
    const act = async (fn, ok) => { try { await fn(); say(true, ok); onChanged(); } catch (err) { say(false, errorText(err, 'That did not work')); onChanged(); } };
    const { ask, dialogs: rowDialogs } = useDialogs();
    const move = async () => {
        const d = await ask({ title: `New last working day for ${e.user.name}`, message: 'Their exit checklist dates move with it.', label: 'Last working day', type: 'date', required: true, defaultValue: e.lastWorkingDay, confirmText: 'Change' });
        if (d === null || d === e.lastWorkingDay) return;
        act(() => api.patch(`/lifecycle/exit/${e.id}`, { lastWorkingDay: d }), `Last working day changed for ${e.user.name}`);
    };
    const withdraw = async () => {
        const reason = await ask({ title: `Withdraw ${e.user.name}'s exit?`, message: 'They go back to being a normal employee and their exit checklist is cancelled.', required: true, confirmText: 'Withdraw exit' });
        if (reason === null) return;
        act(() => api.post(`/lifecycle/exit/${e.id}/withdraw`, { reason }), `Withdrew the exit for ${e.user.name}`);
    };
    const attachFile = (ev) => {
        const file = ev.target.files[0];
        ev.target.value = '';
        if (!file) return;
        if (file.size > MAX_BYTES) { say(false, 'The file must be under 10 MB'); return; }
        const data = new FormData();
        data.append('file', file);
        act(() => api.post(`/lifecycle/exit/${e.id}/evidence`, data, { headers: { 'Content-Type': 'multipart/form-data' } }), `Attached the email for ${e.user.name}`);
    };
    // Opens the settlement, preparing it first if there is none yet.
    const openSettlement = async () => {
        if (e.settlement) { navigate(`/settlements/${e.settlement.id}`); return; }
        try {
            const r = await api.post('/lifecycle/settlements', { separationId: e.id });
            navigate(`/settlements/${r.data.id}`);
        } catch (err) {
            if (err.response?.data?.id) { navigate(`/settlements/${err.response.data.id}`); return; }
            say(false, errorText(err, 'Could not prepare the settlement'));
        }
    };
    const inNotice = e.status === 'APPROVED';
    const settleable = ['APPROVED', 'COMPLETED'].includes(e.status);
    const own = e.userId === viewer.id;
    return (
        <li data-testid={`exit-${e.user.name}`} style={{ borderTop: rowBorder }}>
            {rowDialogs}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', padding: '10px 0' }}>
                <button onClick={() => setOpen(!open)} aria-expanded={open} style={{ background: 'none', border: 0, cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'flex-start', textAlign: 'left', color: 'var(--text-main)' }}>
                    {open ? <ChevronDown size={16} style={{ marginTop: 3 }} /> : <ChevronRight size={16} style={{ marginTop: 3 }} />}
                    <span>
                        <strong>{e.user.name}</strong> <span style={muted}>· {e.kindLabel} · last day {dateWithYear(e.lastWorkingDay)}</span>
                        <span style={{ display: 'block', ...muted }}>
                            {!e.canChange ? (own ? 'Someone else handles your own exit' : 'Handled by an Admin')
                                : inNotice && !e.exitCaseId ? 'No exit checklist yet: use "Start a checklist" at the top of this page'
                                    : e.shortfallDays > 0 ? `${e.shortfallDays} days short of notice, ${e.shortfallRecovery === 'RECOVER' ? 'to be recovered' : 'recovery waived'}` : ''}
                        </span>
                    </span>
                </button>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <ExitBadge status={e.status} />
                    {e.evidenceDocId && <a style={ghost} href={viewUrl(e.evidenceDocId)} target="_blank" rel="noopener noreferrer" aria-label={`Open the email for ${e.user.name}`}><ExternalLink size={14} /> Email</a>}
                    {e.exitCaseId && <Link to={`/admin/checklists?case=${e.exitCaseId}`} style={ghost}>Exit checklist</Link>}
                    {settleable && e.canChange && <button style={ghost} onClick={openSettlement} aria-label={`Final settlement of ${e.user.name}`}><Calculator size={14} /> {e.settlement ? SETTLEMENT[e.settlement.status] : 'Prepare settlement'}</button>}
                    {settleable && e.canChange && (
                        <>
                            <input ref={attach} type="file" accept={FILE_TYPES} hidden aria-label={`Email file for ${e.user.name}`} onChange={attachFile} />
                            <button style={ghost} onClick={() => attach.current?.click()} aria-label={`${e.evidenceDocId ? 'Replace' : 'Attach'} the email for ${e.user.name}`}><Paperclip size={14} /> {e.evidenceDocId ? 'Replace email' : 'Attach email'}</button>
                        </>
                    )}
                    {inNotice && e.canChange && <button style={ghost} onClick={move} aria-label={`Change last day of ${e.user.name}`}><CalendarClock size={14} /> Change date</button>}
                    {['PENDING_APPROVAL', 'APPROVED'].includes(e.status) && e.canChange && <button style={ghost} onClick={withdraw} title="Withdraw this exit" aria-label={`Withdraw exit of ${e.user.name}`}><Ban size={14} /> Withdraw</button>}
                </div>
            </div>
            {open && (
                <div style={{ padding: '0 0 14px 24px', display: 'grid', gap: 8 }}>
                    <div><div style={muted}>{e.kind === 'RESIGNATION' ? 'Reason given' : 'Reason'}</div><div style={{ whiteSpace: 'pre-line', color: 'var(--text-main)' }}>{e.reason}</div></div>
                    <div style={muted}>
                        {e.kind === 'RESIGNATION' ? `Resigned ${dateWithYear(e.resignedOn)} · ` : ''}Notice {e.noticeDays} days{e.onProbation ? ' (probation)' : ''}
                        {e.withdrawnReason ? ` · withdrawn: ${e.withdrawnReason}` : ''}
                    </div>
                    {!e.evidenceDocId && e.canChange && <div style={muted}>No email is attached yet{e.kind === 'RESIGNATION' ? '' : ' (a supporting file is optional)'}.</div>}
                </div>
            )}
        </li>
    );
};

const ExitsPanel = ({ users, say }) => {
    const [status, setStatus] = useState('OPEN');
    const [data, setData] = useState(null);
    const [form, setForm] = useState(false);
    const load = useCallback(() => api.get('/lifecycle/exit', { params: { status } }).then(r => setData(r.data)).catch(e => say(false, errorText(e, 'Could not load exits'))), [status, say]);
    useEffect(() => { load(); }, [load]);
    return (
        <section className="card" style={card} aria-label="Exits">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ fontSize: 18, margin: 0 }}>Exits</h2>
                    <p style={{ ...muted, margin: '2px 0 0' }}>When someone resigns by email, record it here with their email attached.</p>
                </div>
                {!form && data && <button className="btn btn-primary" style={primary} onClick={() => setForm(true)}><LogOut size={14} /> Record an exit</button>}
            </div>
            {form && data && <div style={{ borderTop: rowBorder, marginTop: 12, paddingTop: 12 }}><RecordForm users={users} kinds={data.kinds} kindLabels={data.kindLabels} today={data.today} say={say} onCancel={() => setForm(false)} onDone={() => { setForm(false); setStatus('OPEN'); load(); }} /></div>}
            <div role="tablist" style={{ display: 'flex', gap: 6, margin: '12px 0 4px', flexWrap: 'wrap' }}>
                {[['OPEN', 'In notice'], ['COMPLETED', 'Left'], ['WITHDRAWN', 'Withdrawn']].map(([k, l]) => (
                    <button key={k} role="tab" aria-selected={status === k} style={{ ...ghost, background: status === k ? 'var(--primary)' : 'transparent', color: status === k ? 'var(--on-primary, #fff)' : 'var(--text-main)' }} onClick={() => setStatus(k)}>{l}</button>
                ))}
            </div>
            {!data ? <p style={muted}>Loading…</p> : data.exits.length === 0 ? <p style={{ ...muted, marginBottom: 0 }}>None.</p> : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{data.exits.map(e => <ExitRow key={e.id} e={e} onChanged={load} say={say} />)}</ul>
            )}
        </section>
    );
};

export default ExitsPanel;
